import { Alert, Button, Card, Divider, Modal, Select, Spin, Tag } from "@arco-design/web-react";
import { ArrowLeft, ArrowRight, Bot, ChevronRight, GitCompare, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { comparisonMoney, useComparisonSearch, useQuoteBudgetVersionCompareRuntime } from "../runtime/useQuoteBudgetVersionCompareRuntime";
import type { ComparisonSelection } from "../runtime/useQuoteBudgetVersionCompareRuntime";
import type { QuoteBudgetComparisonItinerary } from "../types";

/** 核价版本状态中文标签。 */
function statusText(status: string) {
  return ({ finalized: "已定稿", draft: "草稿", contract: "合同版本" } as Record<string, string>)[status] ?? status;
}

/** 收集仍为草稿的对比侧，供生成前提示是否先定稿。 */
export function draftVersionsNeedingComparePrompt(
  left?: ComparisonSelection,
  right?: ComparisonSelection,
): Array<{ budgetOrderId: string; versionNo: number }> {
  return [left, right]
    .filter((side): side is ComparisonSelection => Boolean(side) && side.version.status === "draft")
    .map((side) => ({ budgetOrderId: side.version.budgetOrderId, versionNo: side.version.versionNo }));
}

/** 优先回对比侧草稿编辑器；两侧都是草稿时取右侧。 */
export function preferredDraftBudgetOrderId(
  drafts: Array<{ budgetOrderId: string; versionNo: number }>,
  rightBudgetOrderId?: string,
): string | null {
  if (!drafts.length) return null;
  return drafts.find((item) => item.budgetOrderId === rightBudgetOrderId)?.budgetOrderId
    ?? drafts[drafts.length - 1]?.budgetOrderId
    ?? null;
}

/** 模型常把对比侧写死为 V2；按当前所选版本改表头与说明，不改表格数据格。 */
export function alignCompareSummaryVersions(summary: string, leftVersionNo: number, rightVersionNo: number): string {
  if (!Number.isInteger(leftVersionNo) || !Number.isInteger(rightVersionNo) || leftVersionNo < 1 || rightVersionNo < 1) return summary;
  const leftHeader = `V${leftVersionNo}（基准）`;
  const rightHeader = `V${rightVersionNo}（待核对）`;
  const lines = summary.split("\n");
  const separator = lines.findIndex((line) => /^\s*\|(?:\s*:?-{3,}:?\s*\|)+\s*$/.test(line));
  if (separator > 0) {
    lines[separator - 1] = lines[separator - 1].split("|").map((cell) => {
      const text = cell.trim();
      if (text.includes("基准")) return ` ${leftHeader} `;
      if (text.includes("待核对")) return ` ${rightHeader} `;
      return cell;
    }).join("|");
  }
  const next = lines.join("\n");
  if (leftVersionNo === 2 || rightVersionNo === 2) return next;
  let proseStart = 0;
  if (separator >= 0) {
    let index = separator + 1;
    while (index < lines.length && lines[index].trim().startsWith("|")) index += 1;
    proseStart = lines.slice(0, index).join("\n").length;
  }
  return next.slice(0, proseStart) + next.slice(proseStart).replace(/(?<![A-Za-z0-9])V2(?!\d)/g, `V${rightVersionNo}`);
}

/** 双栏展示真实核价版本与费用项目差异清单。 */
export function QuoteBudgetVersionCompareFeature({ budgetOrderId }: { budgetOrderId: string }) {
  const navigate = useNavigate();
  const runtime = useQuoteBudgetVersionCompareRuntime(budgetOrderId);
  const { left, right } = runtime;
  /** 草稿版本先提示是否定稿；对比始终只读已落库金额。 */
  function requestGenerate() {
    if (!runtime.canGenerate || runtime.generating) return;
    const drafts = draftVersionsNeedingComparePrompt(left, right);
    if (!drafts.length) {
      runtime.generate();
      return;
    }
    const labels = drafts.map((item) => `V${item.versionNo}`).join("、");
    const targetId = preferredDraftBudgetOrderId(drafts, right?.version.budgetOrderId);
    Modal.confirm({
      title: "所选版本仍为草稿",
      content: `版本对比只读取已保存到数据库的金额。当前 ${labels} 为草稿；若编辑器里还有未落库调整，金额会仍像复制来源版本。是否先回编辑器保存并定稿？`,
      okText: "仍对比草稿",
      cancelText: "去定稿",
      onOk: () => { runtime.generate(); },
      onCancel: () => { if (targetId) navigate(`/quote/budgets/${targetId}`); },
    });
  }
  return (
    <main className="quote-version-compare" aria-label="核价版本对比工具">
      <header className="quote-version-compare__topbar">
        <Button type="text" icon={<ArrowLeft size={17} />} onClick={() => navigate(`/quote/budgets/${budgetOrderId}`)}>返回行程核价</Button>
        <div className="quote-version-compare__crumbs"><span>行程核价</span><ChevronRight size={14} /><strong>版本对比</strong></div>
        <Tag color="arcoblue">核价版本对比</Tag>
      </header>
      <section className="quote-version-compare__hero">
        <div>
          <p className="quote-version-compare__eyebrow"><GitCompare size={15} /> 核价工具</p>
          <h1>版本对比</h1>
          <p>选择任意两个行程核价版本，只看费用项目的变化，逐项核对新增、缺少和调整内容。</p>
        </div>
        <div className="quote-version-compare__hero-note"><Sparkles size={18} /><span>默认选择当前行程 V1 与最新版本</span></div>
      </section>
      {runtime.loadError ? (
        <section className="quote-version-compare__summary-card"><Alert type="error" content="核价版本加载失败，请重试。" /><Button onClick={runtime.retry}>重新加载</Button></section>
      ) : runtime.loading ? (
        <section className="quote-version-compare__summary-card"><Spin /> 正在加载核价版本…</section>
      ) : (
        <>
          <section className="quote-version-compare__workspace">
            <SelectionCard side="left" selection={left} current={runtime.current} disabled={runtime.generating}
              onItinerary={(item) => runtime.selectItinerary("left", item)} onVersion={(id) => runtime.selectVersion("left", id)} />
            <div className="quote-version-compare__versus" aria-hidden="true"><span>VS</span><div><ArrowRight size={18} /></div></div>
            <SelectionCard side="right" selection={right} current={runtime.current} disabled={runtime.generating}
              onItinerary={(item) => runtime.selectItinerary("right", item)} onVersion={(id) => runtime.selectVersion("right", id)} />
          </section>
          <section className="quote-version-compare__summary-card">
            <div className="quote-version-compare__section-heading">
              <div><p className="quote-version-compare__eyebrow">对比预览</p><h2>费用项目差异</h2></div>
              <Button type="primary" icon={<Bot size={16} />} loading={runtime.generating} disabled={!runtime.canGenerate} onClick={requestGenerate}>
                {runtime.summary ? "重新生成费用项目差异" : "生成费用项目差异"}
              </Button>
            </div>
            <p className="quote-version-compare__hint">只列两侧有变化的费用项目；差异过多时按影响优先级最多列 15 条。编辑器中未保存的调整请先保存；所选为草稿时，生成前会提示是否先定稿。</p>
            {runtime.error ? <Alert type="error" showIcon content={runtime.error} /> : null}
            <Divider />
            <div className="quote-version-compare__ai-result" aria-live="polite">
              <div className="quote-version-compare__ai-header">
                <div className="quote-version-compare__ai-icon"><Bot size={20} /></div>
                <span className="quote-version-compare__ai-label">DeepSeek Flash · 费用项目差异清单</span>
              </div>
              {!runtime.summary ? <p>{runtime.generating ? "正在逐项核对两份核价数据，请稍候…" : runtime.sameVersion ? "请选择两个不同的核价版本后生成费用项目差异。" : "选择好两侧版本后，点击“生成费用项目差异”查看需要人工核对的变化。"}</p> : null}
            </div>
            {runtime.summary ? <div className="quote-version-compare__ai-markdown" data-testid="quote-version-compare-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={{
                  table: ({ children }) => <div className="quote-version-compare__ai-table-wrap"><table>{children}</table></div>,
                }}
              >{alignCompareSummaryVersions(runtime.summary, left?.version.versionNo ?? 0, right?.version.versionNo ?? 0)}</ReactMarkdown>
            </div> : null}
            <p className="quote-version-compare__hint">请结合实际安排判断是否多录、少录或需要调整，再到核价编辑器修改对应项目。</p>
            <div className="quote-version-compare__metrics">
              <Metric label="行程版本" value={`${left ? `V${left.version.versionNo}` : "—"} → ${right ? `V${right.version.versionNo}` : "—"}`} detail={left?.itinerary.itineraryId === right?.itinerary.itineraryId ? "同一行程内对比" : "跨行程对比"} />
              <Metric label="对比状态" value={runtime.generating ? "生成中" : runtime.sameVersion ? "请选择不同版本" : runtime.summary ? "已生成" : "待生成"} detail={runtime.sameVersion ? "也可搜索其他行程进行比较" : "只列费用项目差异，最多 15 条"} warning={runtime.sameVersion} />
              <Metric label="待核对版本总费用（辅助）" value={runtime.rightAmount} detail={runtime.deltaText} warning={runtime.amountIncreased} />
            </div>
          </section>
        </>
      )}
    </main>
  );
}

/** 单侧行程远程搜索和版本本地搜索选择。 */
function SelectionCard({ side, selection, current, disabled, onItinerary, onVersion }: {
  side: "left" | "right";
  selection?: ComparisonSelection;
  current?: QuoteBudgetComparisonItinerary;
  disabled: boolean;
  onItinerary: (item: QuoteBudgetComparisonItinerary) => void;
  onVersion: (id: string) => void;
}) {
  const search = useComparisonSearch();
  const choices = [...new Map([current, selection?.itinerary, ...search.items].filter((item): item is QuoteBudgetComparisonItinerary => Boolean(item)).map((item) => [item.itineraryId, item])).values()];
  const { itinerary, version } = selection ?? {};
  const title = side === "left" ? "基准版本" : "对比版本";
  return (
    <Card className={`quote-version-compare__selection quote-version-compare__selection--${side}`} bordered={false}>
      <div className="quote-version-compare__selection-heading"><div><span className="quote-version-compare__selection-eyebrow">{side === "left" ? "A · 原始口径" : "B · 目标口径"}</span><h2>{title}</h2></div><span className="quote-version-compare__selection-index">{side === "left" ? "01" : "02"}</span></div>
      <div className="quote-version-compare__selection-fields">
        <div>
          <label><span>选择行程</span>
            <Select aria-label={`${title}行程`} showSearch value={itinerary?.itineraryId} disabled={disabled} loading={search.loading}
              placeholder="输入行程名称搜索" onSearch={search.search} filterOption={false}
              options={choices.map((item) => ({ label: item.itineraryTitle, value: item.itineraryId }))}
              onChange={(id) => { const next = choices.find((item) => item.itineraryId === id); if (next) onItinerary(next); }}
              notFoundContent={search.loading ? "正在搜索…" : search.input.trim() ? "未找到有核价版本的行程" : "输入行程名称搜索"} />
          </label>
        </div>
        <label>
          <span>选择核价版本</span>
          <Select aria-label={`${title}核价版本`} showSearch value={version?.budgetOrderId} disabled={disabled || !itinerary}
            placeholder="搜索版本号或名称" onChange={onVersion}
            options={itinerary?.versions.map((item) => ({ label: `V${item.versionNo} · ${item.versionName} · ${statusText(item.status)}`, value: item.budgetOrderId })) ?? []}
            filterOption={(input, option) => String(option?.label ?? "").toLowerCase().includes(input.toLowerCase())} />
        </label>
      </div>
      {search.failed ? <Alert type="error" content={<span>行程搜索失败 <Button type="text" onClick={search.retry}>重试</Button></span>} /> : null}
      {search.limited ? <p className="quote-version-compare__hint">仅显示最近 20 个匹配行程，请输入更完整的名称。</p> : null}
      {version ? <div className="quote-version-compare__version-preview"><strong>V{version.versionNo}</strong><strong>{comparisonMoney(version.costBudgetTotalAmount)}</strong></div> : null}
    </Card>
  );
}

/** 展示当前比较组合的金额或状态。 */
function Metric({ label, value, detail, warning }: { label: string; value: string; detail: string; warning?: boolean }) {
  return <div className="quote-version-compare__metric"><span>{label}</span><strong>{value}</strong><small className={`quote-version-compare__metric-detail quote-version-compare__metric-detail--${warning ? "warning" : "success"}`}>{detail}</small></div>;
}
